import React, { useMemo } from "react";
import { transformToGroupedRows } from "./transformToGroupedRows";
import { formatPeso } from "./formatPeso";

const COLUMN_COUNT = 10;

/**
 * GroupedInventoryTable — Renders inventory data as grouped rows per product.
 *
 * Each product is rendered as a visual group with one sub-row per bodega.
 * Product identity columns (Id, Código, Marca, Nombre, Peso) use rowSpan
 * to span all bodega rows within the group.
 *
 * Props:
 * - data: Array<Product> — Raw product records from API
 * - bodegas: Array<{ idBodega: number, nombre: string }> — Ordered bodega list
 * - loading: boolean — Shows skeleton when true
 * - emptyMessage: string — Message when data is empty
 */
export function GroupedInventoryTable({
  data,
  bodegas,
  loading,
  emptyMessage = "No hay datos de inventario disponibles.",
}) {
  const groupedRows = useMemo(
    () => transformToGroupedRows(data, bodegas),
    [data, bodegas]
  );

  if (loading) {
    return (
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <TableHeader />
          <tbody className="bg-white divide-y divide-gray-200">
            {Array.from({ length: 5 }).map((_, i) => (
              <tr key={`skeleton-${i}`} className="animate-pulse">
                {Array.from({ length: COLUMN_COUNT }).map((_, j) => (
                  <td key={j} className="px-4 py-3">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (!groupedRows || groupedRows.length === 0) {
    return (
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <TableHeader />
          <tbody className="bg-white">
            <tr>
              <td
                colSpan={COLUMN_COUNT}
                className="px-4 py-8 text-center text-gray-500"
              >
                {emptyMessage}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="min-w-full divide-y divide-gray-200">
        <TableHeader />
        <tbody>
          {groupedRows.map((group, groupIndex) => {
            const { product, bodegaRows } = group;
            const bgClass = groupIndex % 2 === 0 ? "bg-white" : "bg-gray-50";
            const borderClass =
              groupIndex > 0 ? "border-t-2 border-gray-300" : "";

            return bodegaRows.map((row, rowIndex) => {
              const isFirstRow = rowIndex === 0;

              return (
                <tr
                  key={`${product.id}-${row.bodegaId}`}
                  className={`${bgClass}${isFirstRow ? ` ${borderClass}` : ""}`}
                >
                  {isFirstRow && (
                    <>
                      <td
                        rowSpan={bodegaRows.length}
                        className="px-4 py-2 text-sm text-gray-700 align-middle"
                      >
                        {product.id}
                      </td>
                      <td
                        rowSpan={bodegaRows.length}
                        className="px-4 py-2 text-sm text-gray-700 align-middle"
                      >
                        {product.codigoProveedor}
                      </td>
                      <td
                        rowSpan={bodegaRows.length}
                        className="px-4 py-2 text-sm text-gray-700 align-middle"
                      >
                        {product.marca}
                      </td>
                      <td
                        rowSpan={bodegaRows.length}
                        className="px-4 py-2 text-sm text-gray-700 align-middle"
                      >
                        {product.nombreProducto}
                      </td>
                      <td
                        rowSpan={bodegaRows.length}
                        className="px-4 py-2 text-sm text-gray-700 align-middle"
                      >
                        {formatPeso(product.pesoKg)}
                      </td>
                    </>
                  )}
                  <td className="px-4 py-1 text-sm text-gray-700">
                    {row.bodegaNombre}
                  </td>
                  <MetricCell value={row.existencia} />
                  <MetricCell value={row.transito} />
                  <MetricCell value={row.vendidoUltimos30Dias} />
                  <MetricCell value={row.vendidoHistorico} />
                </tr>
              );
            });
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Table header with the 10 fixed columns.
 */
function TableHeader() {
  const headers = [
    "Id",
    "Código",
    "Marca",
    "Nombre",
    "Peso (Kg)",
    "Bodega",
    "Inventario real a hoy",
    "Tránsito",
    "Mes móvil",
    "Histórico",
  ];

  return (
    <thead className="bg-gray-50">
      <tr>
        {headers.map((label) => (
          <th
            key={label}
            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
          >
            {label}
          </th>
        ))}
      </tr>
    </thead>
  );
}

/**
 * Renders a metric value cell. Zero values get a muted style.
 */
function MetricCell({ value }) {
  const isZero = value === 0;
  return (
    <td
      className={`px-4 py-1 text-sm ${
        isZero ? "text-gray-400" : "text-gray-700"
      }`}
    >
      {value}
    </td>
  );
}

export default GroupedInventoryTable;
